import type {
  AccommodationType,
  IngredientId,
  SelectedTicketInfo,
} from '@libs/trip-planner-interfaces';
import { exhaustiveGuard } from '@libs/utils';

import type { ItinerarySegmentType } from './itinerarySegmentType';
import type {
  ProductInfo,
  ProductRating,
  UnavailabilityReason,
} from './shared';

export const enum ItineraryActivitySegmentStatus {
  NoAvailable = 'noAvailable',
  Available = 'availableEnabled',
  AlternativeTicket = 'alternativeTicket',
}

export interface ActivityProductCardContent {
  header: string;
  subheader: string;
  rating: ProductRating;
  cardDetails: ProductInfo[];
  changeButtonText: string;
}

export interface ActivityProductCardData {
  imgSrcs: string[];
  productCardContent: ActivityProductCardContent;
}

export interface ActivitySegmentContext {
  parentIngredientId: IngredientId;
  ingredientId: IngredientId;
  ingredientIndex: number;
  hotelCode: string;
  accommodationType: AccommodationType;
}

export interface AvailableItineraryActivitySegment {
  isAvailable: true;
  segmentType: ItinerarySegmentType.Activity;
  statusType: ItineraryActivitySegmentStatus.Available;
  productCardData: ActivityProductCardData;
  context: ActivitySegmentContext;
  date: string;
}

interface UnavailableActivitySegmentContext {
  parentIngredientId: IngredientId;
  ingredientId: IngredientId;
  ingredientIndex: number;
}

export interface UnavailableItineraryActivitySegment {
  isAvailable: false;
  segmentType: ItinerarySegmentType.Activity;
  statusType: ItineraryActivitySegmentStatus.NoAvailable;
  productCardData: null;
  context: UnavailableActivitySegmentContext;
  reasons: UnavailabilityReason[];
  date: string;
}

export interface AlternativeTicketSuggestionActivitySegment {
  isAvailable: false;
  segmentType: ItinerarySegmentType.Activity;
  statusType: ItineraryActivitySegmentStatus.AlternativeTicket;
  productCardData: ActivityProductCardData;
  context: ActivitySegmentContext;
  alternativeSuggestionData: AlternativeActivitySuggestionData;
  date: string;
}

export interface AlternativeActivitySuggestionData {
  tickets: SelectedTicketInfo[];
}

export type ItineraryActivitySegment =
  | AvailableItineraryActivitySegment
  | UnavailableItineraryActivitySegment
  | AlternativeTicketSuggestionActivitySegment;

export function isAvailableActivitySegment(
  segment: ItineraryActivitySegment
): segment is AvailableItineraryActivitySegment {
  switch (segment.statusType) {
    case ItineraryActivitySegmentStatus.Available:
      return true;

    case ItineraryActivitySegmentStatus.NoAvailable:
    case ItineraryActivitySegmentStatus.AlternativeTicket:
      return false;

    default:
      exhaustiveGuard(segment, 'Not known segment type');
  }
}

export function isUnavailableActivitySegment(
  segment: ItineraryActivitySegment
): segment is UnavailableItineraryActivitySegment {
  switch (segment.statusType) {
    case ItineraryActivitySegmentStatus.NoAvailable:
      return true;

    case ItineraryActivitySegmentStatus.Available:
    case ItineraryActivitySegmentStatus.AlternativeTicket:
      return false;

    default:
      exhaustiveGuard(segment, 'Not known segment type');
  }
}

export function isAlternativeTicketSuggestionActivitySegment(
  segment: ItineraryActivitySegment
): segment is AlternativeTicketSuggestionActivitySegment {
  switch (segment.statusType) {
    case ItineraryActivitySegmentStatus.AlternativeTicket:
      return true;

    case ItineraryActivitySegmentStatus.Available:
    case ItineraryActivitySegmentStatus.NoAvailable:
      return false;

    default:
      exhaustiveGuard(segment, 'Not known segment type');
  }
}
